import { useMemo } from 'react';
import { countBy, toString } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';
import { OPMInputSet, OPMInputSetSource } from 'api';
import {
  breakpointAnalysisUrl,
  captableUrl,
  cashFlowLedgerUrl,
  financialUrl,
  firmSummaryUrl,
  firmSummaryViewUrl,
  fundOwnershipUrl,
  valuationUrl,
} from 'common/config/urls';
import {
  PDF_EXPORT_QUERY_PARAM_EXPORT_DESCRIPTION_PAGE,
  PDF_EXPORT_QUERY_PARAM_EXPORT_MODE,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TAB,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET,
} from 'common/constants/pdfExport';
import { useStore } from 'common/store';
import { CapTableVersion } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import { ValuationByAllocation } from 'common/types/valuation';
import { getCustomViewSlug } from 'pages/Intelligence/intelligenceNavigation';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { packDescriptionsForPdf } from 'pages/Valuations/util/packDescriptionsForPdf';
import { sortGptTransactionsForPdfDescriptions } from 'pages/Valuations/util/sortComparisonsForPdfExport';
import { getOPMSourceTitle } from 'pages/ValuationsAllocation/approaches/OPMCalculator/constants';
import {
  OPM_CALCULATOR_QUERY_PARAM_CAP_TABLE,
  OPM_CALCULATOR_QUERY_PARAM_EQUITY_VALUE,
  OPM_CALCULATOR_QUERY_PARAM_MATURITY,
  OPM_CALCULATOR_QUERY_PARAM_OPM_DATE,
  OPM_CALCULATOR_QUERY_PARAM_RISK_FREE_RATE,
  OPM_CALCULATOR_QUERY_PARAM_VOLATILITY,
  VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { toPdfExportTabId } from 'pages/ValuationsAllocation/util';
import {
  CompanyPdfOptions,
  FirmPdfCustomView,
  PdfExportConfiguration,
  PdfExportOption,
} from '../PdfExportConfigDialog.types';
import { ApproachExportItem, isApproachItemSelected } from './getApproachExportItems';
import getReferencedCapTableVersions from './getReferencedCapTableVersions';
import {
  CompanyPdfPageEntry,
  PdfExportSupportedQueryParams,
  PdfExportTargetMapping,
  PdfPage,
} from './types';

const withQuery: (path: string, params: PdfExportSupportedQueryParams) => string = (path, params) => {
  const url = new URL(path, window.location.origin);
  if (params.date) {
    url.searchParams.set('date', params.date);
  }
  if (params.version) {
    url.searchParams.set('version', params.version);
  }
  if (params.exportMode) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_MODE, params.exportMode);
  }
  if (params.exportTarget) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET, params.exportTarget);
  }
  if (params.exportTab) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_TAB, params.exportTab);
  }
  if (params.exportDescriptionPage) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_DESCRIPTION_PAGE, params.exportDescriptionPage);
  }
  if (params.opmInputs) {
    url.searchParams.set(OPM_CALCULATOR_QUERY_PARAM_CAP_TABLE, params.opmInputs.capTableId);
    url.searchParams.set(OPM_CALCULATOR_QUERY_PARAM_EQUITY_VALUE, params.opmInputs.equityValue);
    url.searchParams.set(OPM_CALCULATOR_QUERY_PARAM_RISK_FREE_RATE, params.opmInputs.riskFreeRate);
    url.searchParams.set(OPM_CALCULATOR_QUERY_PARAM_MATURITY, params.opmInputs.maturity);
    url.searchParams.set(OPM_CALCULATOR_QUERY_PARAM_VOLATILITY, params.opmInputs.volatility);
    if (params.opmInputs.opmDate) {
      url.searchParams.set(OPM_CALCULATOR_QUERY_PARAM_OPM_DATE, params.opmInputs.opmDate);
    }
  }
  return url.toString();
};

// The surface a set is named after: an approach over the Equity Allocation scenarios that mirror
// it, and either over a calculator.
const OPM_PAGE_TITLE_SOURCE_PRIORITY: string[] = [
  OPMInputSetSource.source_type.FUTURE_EXIT,
  OPMInputSetSource.source_type.BACKSOLVE,
  OPMInputSetSource.source_type.ALLOCATION_SCENARIO,
];

const getOPMCalculationsSourceTitle = (inputSet: OPMInputSet) => {
  const sourceType = OPM_PAGE_TITLE_SOURCE_PRIORITY.find(type =>
    inputSet.sources.some(source => source.source_type === type)
  );
  // A set only an unlinked calculator uses names no surface.
  return getOPMSourceTitle(sourceType) ?? PdfExportTargets.OPM_CALCULATIONS.title;
};

// "OPM - <surface>" per set; sets sharing a title (two Future Exits) are numbered among themselves.
const getOPMCalculationsPageTitles = (inputSets: OPMInputSet[]) => {
  const titles = inputSets.map(getOPMCalculationsSourceTitle);
  const totals = countBy(titles);
  const seen: Record<string, number> = {};
  return titles.map(title => {
    if (totals[title] <= 1) {
      return title;
    }
    seen[title] = (seen[title] ?? 0) + 1;
    return `${title} (${seen[title]} of ${totals[title]})`;
  });
};

/**
 * Hook to build dynamic URLs for PDF export based on current context and selected configuration
 */
export const usePdfPages = (
  configuration: PdfExportConfiguration,
  valuationInfo?: ValuationByAllocation | null,
  capTableVersions?: CapTableVersion[] | null,
  opmInputSets?: OPMInputSet[] | null,
  firmCustomViews?: FirmPdfCustomView[] | null,
  approachItems?: ApproachExportItem[] | null
): PdfPage[] => {
  const location = useLocation();
  const params = useParams();
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo } = storeValues;

  return useMemo(() => {
    // Build URLs dynamically based on current context and configuration
    const firmSlug = firmInfo?.slug || params.firmSlugParam;
    const companySlug = companyInfo?.slug || params.companySlugParam;
    // Extract current query parameters
    const currentSearchParams = new URLSearchParams(location.search);
    const measurementDate = currentSearchParams.get('date');
    const version = currentSearchParams.get('version');

    // Common query params
    const commonQuery = {
      date: measurementDate,
      version,
      exportMode: 'true',
    } as const;

    // Build URLs based on selected configuration
    const pages: PdfPage[] = [];
    const valuationApproaches = valuationInfo?.valuations_approaches ?? [];

    const opmCalculationsPageTitles = getOPMCalculationsPageTitles(opmInputSets ?? []);

    const pushPage = ({
      urlBuilder,
      target,
      exportDescriptionPage,
      version: versionOverride,
      opmInputs,
    }: CompanyPdfPageEntry) => {
      const exportTab = 'tab' in target ? target.tab : undefined;

      pages.push({
        url: withQuery(urlBuilder(firmSlug!, companySlug!), {
          ...commonQuery,
          // A page that pins its own version (cap table pages) must not inherit the
          // route's version param, which on the Valuations route is an allocation version.
          version: versionOverride ?? commonQuery.version,
          exportTarget: target.id,
          exportTab,
          exportDescriptionPage,
          opmInputs,
        }),
        title: target.title,
        exportTarget: target.id,
        exportTab,
      });
    };

    // Cap Table and Breakpoints are emitted once per non-empty cap table version the valuation
    // uses. Falls back to a single page on the route's own version while the list is still
    // loading; once it has resolved, an empty selection means every referenced version is empty
    // and the pages are dropped rather than captured blank.
    const referencedCapTableVersions = getReferencedCapTableVersions({
      valuationInfo,
      capTableVersions,
      companyOptions: configuration.companyOptions,
      approachOptions: configuration.approachOptions,
    });
    const buildCapTableVersionPages = (
      urlBuilder: (fs: string, cs: string) => string,
      target: PdfExportTargetMapping
    ): CompanyPdfPageEntry[] => {
      if (referencedCapTableVersions.length === 0) {
        return capTableVersions?.length ? [] : [{ urlBuilder, target }];
      }

      return referencedCapTableVersions.map(capTableVersion => ({
        urlBuilder,
        target: {
          ...target,
          // Only name the version when there is more than one page to tell apart.
          title:
            referencedCapTableVersions.length > 1 && capTableVersion.name
              ? `${target.title} – ${capTableVersion.name}`
              : target.title,
        },
        version: capTableVersion.slug || toString(capTableVersion.id),
      }));
    };

    // Fund Ownership and Cash Flow Ledger are restrictProformas pages: they force themselves
    // back to the primary cap table. Pin it explicitly rather than letting them inherit the
    // route's `version`, which off the cap table routes is an allocation slug they cannot
    // match — leaving them to fall back and rewrite the URL inside the capture window.
    const primaryCapTableVersion = capTableVersions?.find(({ is_primary }) => is_primary);
    const primaryVersionParam = primaryCapTableVersion
      ? primaryCapTableVersion.slug || toString(primaryCapTableVersion.id)
      : undefined;

    const companyOptionMapping: Record<keyof CompanyPdfOptions, CompanyPdfPageEntry[]> = {
      valuationSummary: [{ urlBuilder: valuationUrl, target: PdfExportTargets.VALUATION_SUMMARY }],
      equityAllocation: [{ urlBuilder: valuationUrl, target: PdfExportTargets.EQUITY_ALLOCATION }],
      concludedShareValues: [{ urlBuilder: valuationUrl, target: PdfExportTargets.CONCLUDED_SHARE_VALUES }],
      financials: [
        { urlBuilder: financialUrl, target: PdfExportTargets.FINANCIALS },
        { urlBuilder: financialUrl, target: PdfExportTargets.FINANCIALS_BALANCE_SHEET },
      ],
      capTable: buildCapTableVersionPages(captableUrl, PdfExportTargets.CAP_TABLE),
      fundOwnership: [
        { urlBuilder: fundOwnershipUrl, target: PdfExportTargets.FUND_OWNERSHIP, version: primaryVersionParam },
      ],
      breakpointAnalysis: buildCapTableVersionPages(breakpointAnalysisUrl, PdfExportTargets.BREAKPOINT_ANALYSIS),
      cashFlowLedger: [
        { urlBuilder: cashFlowLedgerUrl, target: PdfExportTargets.CASH_FLOW_LEDGER, version: primaryVersionParam },
      ],
      approachDcf: [{ urlBuilder: valuationUrl, target: PdfExportTargets.DISCOUNTED_CASH_FLOW }],
      approachExternalValuation: [{ urlBuilder: valuationUrl, target: PdfExportTargets.EXTERNAL_VALUATION }],
      approachFutureExit: [{ urlBuilder: valuationUrl, target: PdfExportTargets.FUTURE_EXIT }],
      approachTransactionComps: [{ urlBuilder: valuationUrl, target: PdfExportTargets.TRANSACTION_COMPS }],
      approachTransactionCompsDescriptions: valuationApproaches
        .filter(a => a.approach_type === VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH)
        .flatMap(a => {
          const approachTab = toPdfExportTabId('approach', a.approach_type, a.id ?? a.panelId);
          const sortedTransactions = sortGptTransactionsForPdfDescriptions(
            a.valuations_approach_gpt?.gpt_transactions ?? []
          );
          const chunks = packDescriptionsForPdf(sortedTransactions, t => t.business_description);
          if (chunks.length === 0) {
            return [];
          }

          return chunks.map((_, index) => ({
            urlBuilder: valuationUrl,
            target: {
              title: PdfExportTargets.TRANSACTION_COMPS_DESCRIPTIONS.title,
              id: PdfExportTargets.TRANSACTION_COMPS_DESCRIPTIONS.id,
              tab: approachTab,
            },
            exportDescriptionPage: String(index),
          }));
        }),
      approachSpecifiedShareValues: [{ urlBuilder: valuationUrl, target: PdfExportTargets.SPECIFIED_SHARE_VALUES }],
      approachCalibration: [{ urlBuilder: valuationUrl, target: PdfExportTargets.CALIBRATION }],
      approachRangeSensitivity: [{ urlBuilder: valuationUrl, target: PdfExportTargets.RANGE_SENSITIVITY }],
      opmCalculations: (opmInputSets ?? []).map((inputSet, index) => ({
        urlBuilder: valuationUrl,
        target: {
          ...PdfExportTargets.OPM_CALCULATIONS,
          title: opmCalculationsPageTitles[index],
        },
        opmInputs: {
          capTableId: toString(inputSet.cap_table_id),
          equityValue: inputSet.equity_value,
          riskFreeRate: inputSet.risk_free_rate,
          maturity: inputSet.maturity,
          volatility: inputSet.volatility,
          opmDate: inputSet.opm_date,
        },
      })),
    };

    switch (configuration.selectedOption) {
      case PdfExportOption.VALUATIONS:
        if (companySlug) {
          const { companyOptions, companyOptionsOrder, approachOptions } = configuration;
          const approachItemsById = new Map((approachItems ?? []).map(item => [item.id, item]));
          companyOptionsOrder.forEach(key => {
            // Per-approach rows (Public Comps and Backsolve) are independent of each other.
            const approachItem = approachItemsById.get(key);
            if (approachItem) {
              if (isApproachItemSelected(approachOptions, key)) {
                approachItem.pages.forEach(pushPage);
              }
              return;
            }
            const optionKey = key as keyof CompanyPdfOptions;
            if (companyOptions[optionKey] && companyOptionMapping[optionKey]) {
              companyOptionMapping[optionKey].forEach(pushPage);
            }
          });
        }
        break;

      case PdfExportOption.FIRM:
        if (firmSlug) {
          const { firmOptions } = configuration;

          if (firmOptions.summary) {
            pages.push({
              url: withQuery(firmSummaryUrl(firmSlug!), {
                ...commonQuery,
                exportTarget: PdfExportTargets.FIRM_SUMMARY.id,
              }),
              title: PdfExportTargets.FIRM_SUMMARY.title,
              exportTarget: PdfExportTargets.FIRM_SUMMARY.id,
            });
          }

          // Each custom view renders the same firm summary grid on its own tab, so it reuses the
          // Firm Summary capture target. A view without an entry in customViews is selected.
          (firmCustomViews ?? [])
            .filter(view => firmOptions.customViews[view.id] !== false)
            .forEach(view => {
              pages.push({
                url: withQuery(firmSummaryViewUrl(firmSlug!, getCustomViewSlug(view.id)), {
                  ...commonQuery,
                  exportTarget: PdfExportTargets.FIRM_SUMMARY.id,
                }),
                title: view.name,
                exportTarget: PdfExportTargets.FIRM_SUMMARY.id,
              });
            });
        }
        break;

      default:
        // No additional URLs for unknown options
        break;
    }

    return pages;
  }, [
    configuration,
    location.search,
    params,
    companyInfo,
    firmInfo,
    valuationInfo,
    capTableVersions,
    opmInputSets,
    firmCustomViews,
    approachItems,
  ]);
};
